// apps/web/src/components/ui/campo-select.tsx — substitui o <select> nativo nas telas de gestão (2026-10-14).
// O <select> nativo abre a lista do sistema operacional (fundo branco, destaque azul, seta própria), fora do design da
// plataforma. O CampoSelect expõe uma API próxima da nativa (`value`, `onValueChange`, `options`) e renderiza o Select
// da plataforma (Radix), com o mesmo gatilho, seta, lista e teclado dos demais Select.
//
// O Radix proíbe `value=""` em item. Valor vazio ("Todos", "Selecione...") é mapeado para um sentinela interno e
// volta como "" no `onValueChange`, então os chamadores continuam usando "" como "sem seleção".
// O rótulo selecionado é passado como filho do SelectValue: assim o texto aparece também na renderização de servidor
// e nos testes de marcação, sem depender do portal de itens do Radix.
// O tablet do coletor (D-16) continua com <select> nativo, de propósito: lista do sistema com alvo de toque grande.
import type { ReactElement } from 'react';
import { cn } from 'cn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

const SENTINELA_VAZIO = '__campo-select-vazio__';

export interface OpcaoCampoSelect {
  /** Valor devolvido em `onValueChange`; "" significa "sem seleção / todos". */
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface CampoSelectProps {
  /** Valor atual; "" quando nada está escolhido. Se não houver opção com esse valor, mostra o `placeholder`. */
  readonly value: string;
  readonly onValueChange: (valor: string) => void;
  readonly options: readonly OpcaoCampoSelect[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Mesmo `id` do `<label htmlFor>`: vai para o gatilho (botão rotulável). */
  readonly id?: string;
  readonly name?: string;
  readonly required?: boolean;
  readonly className?: string;
  /** `sm` para paginação e barras de filtro compactas. */
  readonly size?: 'sm' | 'default';
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean;
  readonly 'data-testid'?: string;
}

function paraRadix(valor: string): string {
  return valor === '' ? SENTINELA_VAZIO : valor;
}

function dePeloRadix(valor: string): string {
  return valor === SENTINELA_VAZIO ? '' : valor;
}

export function CampoSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Selecione',
  disabled,
  id,
  name,
  required,
  className,
  size = 'default',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
}: CampoSelectProps): ReactElement {
  const selecionada = options.find((opcao) => opcao.value === value);
  return (
    <Select
      value={selecionada ? paraRadix(value) : ''}
      onValueChange={(valor) => onValueChange(dePeloRadix(valor))}
      disabled={disabled}
      name={name}
      required={required}
    >
      <SelectTrigger
        id={id}
        size={size}
        title={selecionada?.label}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid || undefined}
        data-testid={testId}
        className={cn('w-full min-w-0 data-[size=default]:h-[38px] max-lg:data-[size=default]:min-h-11', className)}
      >
        <SelectValue placeholder={placeholder}>{selecionada?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent position="popper" align="start" sideOffset={4}>
        {options.map((opcao) => (
          <SelectItem key={opcao.value} value={paraRadix(opcao.value)} disabled={opcao.disabled}>
            {opcao.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
